import cn from "classnames"
import React, { useEffect, useRef } from "react"
import { withTranslation, type WithTranslation } from "react-i18next"
import { isTablet } from "@/common/util"
import HelpCenter from "@/help_center/components/help_center"
import type Sign from "@/signomatic/app/sign"
import { type actions } from "@/signomatic/store/actions"
import { ChevronLeftLarge, CloseLarge } from "@/skylticons"

export type ToolboxKey =
  | "products"
  | "size"
  | "shape"
  | "fixmethod"
  | "material"
  | "preset"
  | "production_type"
  | "type"
  | "text"
  | "image"
  | "color"
  | "border"
  | "sides"
  | "submenu"
  | "admin"

export interface ToolboxItemOwnProps {
  sign?: Sign
  overflow?: boolean
  className?: string
  index?: number
  children?: React.ReactNode
  forwardedRef?: React.RefObject<HTMLDivElement> | null
  mobile?: boolean
  subToolbox?: string | null
  tKey?: ToolboxKey
  hideHeaderBar?: boolean
  labelKey?: string
}

export interface ToolboxItemStateProps {
  setToolbox: typeof actions.setToolbox
  setToolboxHeight: typeof actions.setToolboxHeight
}

type ToolboxItemProps = ToolboxItemOwnProps & ToolboxItemStateProps & WithTranslation

const ToolboxItem: React.FC<ToolboxItemProps> = ({
  children,
  className,
  overflow,
  forwardedRef,
  mobile,
  index,
  t,
  sign,
  subToolbox,
  setToolbox,
  setToolboxHeight,
  tKey,
  hideHeaderBar,
  labelKey,
}) => {
  const menurow = useRef(document.querySelector<HTMLElement>(".menurow"))
  const menurowFiller = useRef(document.querySelector<HTMLElement>(".menurow-filler"))
  const heightAuto = tKey === "fixmethod" && !!subToolbox

  const subToolboxLabel = () => {
    if (tKey === "fixmethod") {
      return t(`fixmethod.${subToolbox}`)
    }

    if (tKey === "production_type") {
      if (
        ["plastic_sign", "name_sign"].some(value => subToolbox?.includes(value)) &&
        !subToolbox?.includes("solid_plastic")
      ) {
        return t("production_type.laser")
      }

      if (subToolbox?.includes("color_badge")) return t("production_type.print")
      if (subToolbox?.includes("etched")) return t("production_type.laser_etching")

      return t(`production_type.${subToolbox?.replace("_sign_production_type", "")}`)
    } else if (tKey === "preset") {
      if (subToolbox === "label_laser") return t("material_type.stainless")
      if (subToolbox === "label_aluminium") return t("material_type.aluminium")
      if (subToolbox === "label_lacquered_aluminium") return t("material_type.aluminium_lacquered")
      if (subToolbox === "label_anodised_aluminium") return t("material_type.aluminium_anodised")

      return t(`material_type.${subToolbox}`)
    } else if (tKey === "type") {
      return t("sign_type.acryl_alu_sign")
    } else if (tKey === "border" && subToolbox === "color") {
      return t("toolbox.border_color")
    } else if (["border", "text", "image"].includes(tKey || "") && subToolbox === "customcolor") {
      return t("signomatic.custom_color")
    } else if (tKey === "image" && subToolbox === "color") {
      return t("toolbox.color")
    } else if (tKey === "color" && subToolbox === "patterns") {
      return t("toolbox.patterns")
    }

    return t(`sign_type.${subToolbox}`)
  }

  const toolboxLabel = () => {
    if (tKey === "color" && subToolbox === "customcolor") return t("signomatic.custom_color")
    if (tKey === "material" && labelKey === "frosty") return t("material_type.frosty")
    if (tKey === "material" && labelKey === "reflective") return t("material_type.reflective")

    if (tKey === "preset" && ["label_print", "label_laser"].includes(labelKey || "")) {
      return t("toolbox.label_print")
    }

    if (tKey === "preset" && labelKey === "door") return t("toolbox.door")
    if (tKey === "preset" && labelKey === "tags") return t("toolbox.tags")

    return t(`toolbox.${tKey === "preset" ? "presets" : tKey}`)
  }

  const handleDone = (e: React.MouseEvent) => {
    e.stopPropagation()
    sign?.deleteEmptyObjects()

    if (isTablet() && sign) {
      setToolboxHeight(0)
      if (menurow.current) menurow.current.style.display = "flex"
      if (menurowFiller.current) menurowFiller.current.style.display = "block"
    }

    setToolbox(null, index)
  }

  useEffect(() => {
    if (isTablet()) {
      setToolboxHeight(document.querySelector(".toolbox")?.clientHeight || 0)
      if (menurow.current) menurow.current.style.display = "none"
      if (menurowFiller.current) menurowFiller.current.style.display = "none"
    }
  }, [setToolboxHeight])

  return (
    <div
      className={cn(
        "toolbox visible-scrollbar with-done pointer-events-auto absolute bottom-0 left-0 right-0 z-30 flex flex-col bg-white shadow-[0_2px_4px_rgba(0,0,0,0.5)] lg:relative lg:w-80 first:lg:h-full first:lg:shadow-none xl:relative xl:bottom-[initial] xl:left-[initial] xl:right-[initial] xl:top-[initial] xl:w-96 [&:nth-child(2)]:shadow-[0_2px_4px_rgba(0,0,0,0.5)] lg:[&:nth-child(2)]:absolute lg:[&:nth-child(2)]:bottom-auto lg:[&:nth-child(2)]:left-[434px] lg:[&:nth-child(2)]:h-[calc(100vh-280px)] lg:[&:nth-child(2)]:max-h-[640px] xl:[&:nth-child(2)]:left-[538px]",
        `toolbox-${tKey}`,
        className,
        {
          overflow,
          "lg:[&:nth-child(2)]:h-auto": heightAuto,
          "h-[352px] lg:h-full": tKey === "products",
        }
      )}
      ref={forwardedRef}
    >
      {mobile && (
        <div id="help-widget">
          <HelpCenter signomatic />
        </div>
      )}
      <div className="button-group z-20">
        {(index || 0) >= 1 ? (
          <div className="flex h-12 items-center">
            {mobile || (subToolbox === "customcolor" && tKey === "color") ? (
              <button
                className="toolbox-back button button-subtle icon-left mb-0 px-2"
                onClick={handleDone}
                type="button"
              >
                <ChevronLeftLarge className="!mr-1 h-6 w-6" />
                <span className="text-sm font-semibold">{t("toolbox.back")}</span>
              </button>
            ) : (
              <button
                className="toolbox-close button button-subtle icon-only !ml-3 mb-0"
                onClick={handleDone}
                type="button"
              >
                <CloseLarge className="h-6 w-6" />
              </button>
            )}
            <span className="absolute left-1/2 -translate-x-1/2 text-base font-semibold">{subToolboxLabel()}</span>
          </div>
        ) : (
          <div className={cn("flex h-12 items-center justify-start", { hidden: hideHeaderBar })}>
            {subToolbox === "customcolor" && tKey === "color" ? (
              <button
                className="toolbox-back button button-subtle icon-left !ml-3 mb-0 px-2"
                onClick={handleDone}
                type="button"
              >
                <ChevronLeftLarge className="!mr-1 h-6 w-6" />
                <span className="text-sm font-semibold">{t("toolbox.back")}</span>
              </button>
            ) : (
              <button
                className="toolbox-close button button-subtle icon-only !ml-3 mb-0"
                onClick={handleDone}
                type="button"
              >
                <CloseLarge className="h-6 w-6" />
              </button>
            )}
            <span className="absolute right-1/2 translate-x-1/2 text-base font-semibold">{toolboxLabel()}</span>
          </div>
        )}
      </div>
      <div className="toolbox-contents z-10">{children}</div>
    </div>
  )
}

export default withTranslation()(ToolboxItem)
